import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './apiClient.js';

/**
 * RBAC Service
 * Handles API calls for users and roles management
 */

/**
 * Get all users
 * @returns {Promise<Array>} Array of UserSummary
 */
export async function getUsers() {
  return apiGet('/admin/rbac/users');
}

/**
 * Get user by ID
 * @param {string} userId
 * @returns {Promise<Object>} UserDetail
 */
export async function getUser(userId) {
  const response = await apiGet(`/admin/rbac/users/${userId}`);
  return response.user;
}

/**
 * Get all roles
 * @returns {Promise<Array>} Array of RoleSummary
 */
export async function getRoles() {
  return apiGet('/admin/rbac/roles');
}

/**
 * Get role by ID
 * @param {string} roleId
 * @returns {Promise<Object>} RoleDetail
 */
export async function getRole(roleId) {
  const response = await apiGet(`/admin/rbac/roles/${roleId}`);
  return response.role;
}

/**
 * Assign role to user
 * @param {string} userId
 * @param {string} roleId
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function assignRole(userId, roleId, reason) {
  const response = await apiPost(`/admin/rbac/users/${userId}/roles`, {
    roleId,
    reason,
  });
  return response.user;
}

/**
 * Remove role from user
 * @param {string} userId
 * @param {string} roleId
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function removeRole(userId, roleId, reason) {
  // DELETE with body - use fetch directly since apiDelete doesn't support body
  const response = await fetch(`/api/v1/admin/rbac/users/${userId}/roles/${roleId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }

  const data = await response.json();
  return data.user;
}

/**
 * Create a new user
 * @param {string} email
 * @param {string} [displayName]
 * @param {string} [password] - Optional temporary password
 * @param {boolean} [mustResetPassword] - Whether to force password reset (default: true if password provided)
 * @param {string} reason
 * @returns {Promise<Object>} Created UserDetail
 */
export async function createUser(email, displayName, password, mustResetPassword, reason) {
  const body = {
    email,
    display_name: displayName || null,
    reason,
  };
  if (password) {
    body.password = password;
    body.must_reset_password = mustResetPassword !== undefined ? mustResetPassword : true;
  }
  const response = await apiPost('/admin/rbac/users', body);
  return response.user;
}

/**
 * Reset user password (admin action)
 * @param {string} userId
 * @param {string} password
 * @param {boolean} forceReset
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function resetUserPassword(userId, password, forceReset, reason) {
  const response = await apiPost(`/admin/rbac/users/${userId}/password-reset`, {
    password,
    force_reset: forceReset,
    reason,
  });
  return response.user;
}

/**
 * Generate owner password reset link (admin action)
 * Owner-role users only. Use generatePasswordResetLink for any user.
 * @param {string} userId
 * @param {string} reason
 * @returns {Promise<Object>} { resetUrl, expiresAt, userId, email }
 */
export async function generateOwnerResetLink(userId, reason) {
  const response = await apiPost(`/admin/rbac/users/${userId}/owner-reset-link`, {
    reason,
  });
  if (response && response.data && typeof response.data === 'object' && response.data.resetUrl) {
    return response.data;
  }
  if (response && response.resetUrl) {
    return response;
  }
  throw new Error('Unexpected response format from generateOwnerResetLink API');
}

/**
 * Generate password reset link for any user (admin action)
 * Works for superadmin, operator, owner, etc. RBAC: admin:users:reset-password.
 * @param {string} userId
 * @param {string} reason
 * @returns {Promise<Object>} { resetUrl, expiresAt, userId, email }
 */
export async function generatePasswordResetLink(userId, reason) {
  const data = await apiPost(`/admin/users/${userId}/password-reset-link`, {
    reason,
  });
  if (!data || (typeof data.reset_url !== 'string' && typeof data.resetUrl !== 'string')) {
    throw new Error('Unexpected response format from generatePasswordResetLink API');
  }
  return {
    resetUrl: data.reset_url || data.resetUrl,
    expiresAt: data.expires_at || data.expiresAt,
    userId: data.user_id || data.userId,
    email: data.email,
  };
}

/**
 * Get password reset history for a user
 * @param {string} userId
 * @param {number} [limit=5] - Number of items to return (1-10)
 * @returns {Promise<Object>} { items: [...] }
 */
export async function getPasswordResetHistory(userId, limit = 5) {
  const response = await apiGet(`/admin/users/${userId}/password-reset-history?limit=${limit}`);
  return response.items || [];
}

/**
 * Set user's own password (self-service)
 * @param {string} password
 * @returns {Promise<Object>} Success response
 */
export async function setPassword(password) {
  const response = await apiPost('/admin/auth/set-password', {
    password,
  });
  return response;
}

/**
 * Update user
 * @param {string} userId
 * @param {object} fields - {email?, display_name?}
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function updateUser(userId, fields, reason) {
  const response = await apiPut(`/admin/rbac/users/${userId}`, {
    ...fields,
    reason,
  });
  return response.user;
}

/**
 * Activate user
 * @param {string} userId
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function activateUser(userId, reason) {
  // PATCH request - use fetch directly since we don't have apiPatch helper
  const response = await fetch(`/api/v1/admin/rbac/users/${userId}/activate`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }

  const data = await response.json();
  return data.user;
}

/**
 * Deactivate user
 * @param {string} userId
 * @param {string} reason
 * @returns {Promise<Object>} Updated UserDetail
 */
export async function deactivateUser(userId, reason) {
  // PATCH request - use fetch directly since we don't have apiPatch helper
  const response = await fetch(`/api/v1/admin/rbac/users/${userId}/deactivate`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }

  const data = await response.json();
  return data.user;
}

/**
 * Delete user (soft delete)
 * @param {string} userId
 * @param {string} reason
 * @returns {Promise<Object>} Deleted UserDetail
 */
export async function deleteUser(userId, reason) {
  const response = await fetch(`/api/v1/admin/rbac/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }

  const data = await response.json();
  return data.user;
}

/**
 * Create a new role
 * @param {string} name
 * @param {string} [description]
 * @param {string[]} [capabilities]
 * @param {string} reason
 * @returns {Promise<Object>} Created RoleDetail
 */
export async function createRole(name, description, capabilities, reason) {
  const response = await apiPost('/admin/rbac/roles', {
    name,
    description: description || null,
    capabilities: capabilities || [],
    reason,
  });
  return response.role;
}

/**
 * Update role
 * @param {string} roleId
 * @param {object} fields - {name?, description?}
 * @param {string} reason
 * @returns {Promise<Object>} Updated RoleDetail
 */
export async function updateRole(roleId, fields, reason) {
  const response = await apiPut(`/admin/rbac/roles/${roleId}`, {
    ...fields,
    reason,
  });
  return response.role;
}

/**
 * Delete role
 * @param {string} roleId
 * @param {string} reason
 * @returns {Promise<void>}
 */
export async function deleteRole(roleId, reason) {
  await fetch(`/api/v1/admin/rbac/roles/${roleId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });
}

/**
 * Add capability to role
 * @param {string} roleId
 * @param {string} capability
 * @param {string} reason
 * @returns {Promise<Object>} Updated RoleDetail
 */
export async function addCapabilityToRole(roleId, capability, reason) {
  const response = await apiPost(`/admin/rbac/roles/${roleId}/capabilities`, {
    capability,
    reason,
  });
  return response.role;
}

/**
 * Remove capability from role
 * @param {string} roleId
 * @param {string} capability
 * @param {string} reason
 * @returns {Promise<Object>} Updated RoleDetail
 */
export async function removeCapabilityFromRole(roleId, capability, reason) {
  const response = await fetch(`/api/v1/admin/rbac/roles/${roleId}/capabilities/${encodeURIComponent(capability)}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }

  const data = await response.json();
  return data.role;
}

/**
 * Get available properties for owner access assignment
 * @param {string} [search] - Optional search term
 * @returns {Promise<Array>} Array of property objects with clc_listing_id, hostaway_listing_id, clc_short_name
 */
export async function getPropertiesForOwnerAccess(search = '') {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await apiGet(`/admin/rbac/properties${params}`);
  return response.properties || [];
}

/**
 * Get property access assignments for a user
 * @param {string} userId
 * @returns {Promise<Array>} Array of property objects with clc_listing_id, hostaway_listing_id, clc_short_name, assigned_by, created_at
 */
export async function getUserPropertyAccess(userId) {
  const response = await apiGet(`/admin/rbac/users/${userId}/properties`);
  return response.properties || [];
}

/**
 * Assign owner access (atomic: ensures Owner role + replaces property assignments)
 * @param {string} userId
 * @param {string[]} propertyIds - Array of clc_listing_id UUIDs
 * @param {string} reason
 * @returns {Promise<Object>} { user_id, property_count }
 */
export async function assignOwnerAccess(userId, propertyIds, reason) {
  const response = await apiPost(`/admin/rbac/users/${userId}/owner-access`, {
    property_ids: propertyIds,
    reason,
  });
  return response;
}

/**
 * Get site access for a user (which sites they can log into).
 * @param {string} userId
 * @returns {Promise<{ userId, legacyMode, sites: Array<{ siteId, app, environment, host, loginPath, isActive, canLoginForUser }> }>}
 */
export async function getUserSiteAccess(userId) {
  const response = await apiGet(`/admin/rbac/users/${userId}/site-access`);
  return response.data || response;
}

/**
 * Update site access for a user.
 * @param {string} userId
 * @param {Array<{ siteId: string, canLogin: boolean }>} updates
 * @returns {Promise<{ userId, legacyMode, sites }>}
 */
export async function updateUserSiteAccess(userId, updates) {
  const response = await apiPut(`/admin/rbac/users/${userId}/site-access`, { updates });
  return response.data || response;
}

/**
 * Get owner identity + contact details and secondary contacts.
 * @param {string} userId
 * @returns {Promise<{ details: object|null, contacts: Array }>}
 */
export async function getOwnerDetails(userId) {
  const response = await apiGet(`/admin/rbac/users/${userId}/owner-details`);
  return {
    details: response?.details ?? null,
    contacts: Array.isArray(response?.contacts) ? response.contacts : [],
  };
}

/**
 * Upsert owner identity + contact details.
 * @param {string} userId
 * @param {object} fields  owner_details column subset
 * @param {string} reason
 * @returns {Promise<{ details: object|null, contacts: Array }>}
 */
export async function updateOwnerDetails(userId, fields, reason) {
  const response = await apiPut(`/admin/rbac/users/${userId}/owner-details`, {
    ...fields,
    reason,
  });
  return {
    details: response?.details ?? null,
    contacts: Array.isArray(response?.contacts) ? response.contacts : [],
  };
}

/**
 * Create a secondary owner contact (spouse / property manager / etc.).
 */
export async function createOwnerContact(userId, fields, reason) {
  const response = await apiPost(`/admin/rbac/users/${userId}/contacts`, {
    ...fields,
    reason,
  });
  return response?.contact ?? null;
}

/**
 * Update a secondary owner contact.
 */
export async function updateOwnerContact(userId, contactId, fields, reason) {
  const response = await apiPatch(`/admin/rbac/users/${userId}/contacts/${contactId}`, {
    ...fields,
    reason,
  });
  return response?.contact ?? null;
}

/**
 * Delete a secondary owner contact (uses fetch directly because DELETE has a body).
 */
export async function deleteOwnerContact(userId, contactId, reason) {
  const response = await fetch(`/api/v1/admin/rbac/users/${userId}/contacts/${contactId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-DEV-IS-SUPERADMIN': 'true',
      'X-DEV-USER-ID': '00000000-0000-0000-0000-000000000001',
    },
    credentials: 'include',
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }));
    const apiError = new Error(err.message || `HTTP ${response.status}: ${response.statusText}`);
    apiError.status = response.status;
    throw apiError;
  }
  return true;
}

